import { type ApiResponse } from "apisauce"

import { request } from "../../../services/api.ts"
import type { BaseApiResponse } from "../../../types/api.ts"

const PATH = "/user/status-automation-settings/"

export type StatusAutomationMode = "off" | "suggest" | "auto"

export type StatusAutomationParams = Record<string, unknown>

export interface StatusAutomationRuleSetting {
  mode: StatusAutomationMode
  params: StatusAutomationParams
}

export interface StatusAutomationParamSchema {
  type: "string" | "integer" | "array"
  enum?: string[]
  minimum?: number
  maximum?: number
  items?: { type: string; enum?: string[] }
  min_items?: number
  unique_items?: boolean
}

export interface StatusAutomationRuleDefinition {
  key: string
  entity: "lead" | "project"
  // `Project.Status` for a lead rule, `Project.ProjectStatus` for a project
  // rule; null for rules that raise a flag or suggestion instead of a move.
  target: number | null
  allowed_modes: StatusAutomationMode[]
  default_mode: StatusAutomationMode
  params_schema?: Record<string, StatusAutomationParamSchema>
  default_params: StatusAutomationParams
}

export interface StatusAutomationSettings {
  enabled: boolean
  notify_owner: boolean
  auto_create_project_from_lead: boolean
  rules: Record<string, StatusAutomationRuleSetting>
  rule_catalog: StatusAutomationRuleDefinition[]
}

export type StatusAutomationSettingsPatch = Partial<
  Omit<StatusAutomationSettings, "rule_catalog">
>

export interface StatusAutomationPreviewRecord {
  id: string
  uid: string
  name: string
  is_project: boolean
}

export interface StatusAutomationPreviewMove {
  rule: string
  target: number | null
  count: number
  records: StatusAutomationPreviewRecord[]
}

// A refused PATCH names each bad option as `{"rules": {"<rule>.<field>": [msg]}}`.
export interface StatusAutomationSaveErrors {
  rules?: Record<string, string[] | string>
}

export interface StatusAutomationPreview {
  window_days: number
  moves: StatusAutomationPreviewMove[]
}

// Every answer may arrive enveloped (`{status, details: {..., data}}`) —
// callers read them through `unwrapEnvelope`, which also accepts a bare GET.
export const getStatusAutomationSettings = (): Promise<
  ApiResponse<
    StatusAutomationSettings | BaseApiResponse<StatusAutomationSettings>
  >
> => request.api.get(PATH)

export const updateStatusAutomationSettings = (
  payload: StatusAutomationSettingsPatch
): Promise<ApiResponse<BaseApiResponse<StatusAutomationSettings>, unknown>> =>
  request.api.patch(PATH, payload)

export const previewStatusAutomation = (payload: {
  rules?: Record<string, StatusAutomationRuleSetting>
}): Promise<
  ApiResponse<StatusAutomationPreview | BaseApiResponse<StatusAutomationPreview>>
> => request.api.post(`${PATH}preview/`, payload)
